<template>
  <div class="pad-page">
    <!-- 1. Tablet niesparowany: kod przepisywany z ekranu komputera -->
    <div v-if="!token" class="pad-center">
      <div class="pad-card">
        <v-icon size="42" color="primary">mdi-tablet-dashboard</v-icon>
        <h1>Podpis kierowcy</h1>
        <p class="pad-lead">
          Wpisz sześciocyfrowy kod, który wyświetlił się na komputerze obsługi.
        </p>
        <v-text-field
          v-model="code"
          type="tel"
          maxlength="6"
          outlined
          class="pad-code"
          placeholder="000000"
          hide-details
          @keyup.enter="pair"
        />
        <v-alert v-if="error" type="error" text dense class="mt-3">{{ error }}</v-alert>
        <v-btn color="primary" x-large block class="mt-4" :loading="busy" @click="pair">
          Połącz z komputerem
        </v-btn>
      </div>
    </div>

    <!-- 2. Sparowany, czeka na wypłatę -->
    <div v-else-if="!request" class="pad-center">
      <div class="pad-card pad-card--idle">
        <v-icon size="52" color="success">mdi-check-circle-outline</v-icon>
        <h1>Gotowe do podpisu</h1>
        <p class="pad-lead">
          Ten tablet jest połączony z komputerem obsługi.<br />
          Gdy obsługa wyda pieniądze, na ekranie pojawi się kwota do podpisania.
        </p>
        <div class="pad-device">{{ deviceName }}</div>
        <div v-if="!wakeLockSupported" class="pad-hint">
          Ta przeglądarka nie potrafi utrzymać ekranu włączonego — wyłącz
          automatyczne blokowanie ekranu w ustawieniach tabletu, żeby nie
          trzeba było go budzić przy każdej wypłacie.
        </div>
        <div class="pad-idle-actions">
          <v-btn v-if="!fullscreen" outlined @click="enterFullscreen">
            <v-icon left small>mdi-fullscreen</v-icon>Ukryj pasek adresu
          </v-btn>
          <v-btn text small @click="unpair">Odłącz ten tablet</v-btn>
        </div>
        <div v-if="!fullscreen" class="pad-hint pad-hint--calm">
          Na stałe: dodaj tę stronę do ekranu głównego tabletu — uruchomi się wtedy
          jak zwykła aplikacja, bez paska adresu i bez dostępu do reszty panelu.
        </div>
      </div>
    </div>

    <!-- 3. Jest co podpisać -->
    <div v-else class="pad-sign">
      <div class="pad-sign-head">
        <div>
          <div class="pad-driver">{{ request.driverName }}</div>
          <div class="pad-sub">potwierdzam odbiór gotówki</div>
        </div>
        <div class="pad-total">{{ money(request.totalCents) }}</div>
      </div>

      <!-- Kierowca musi widzieć, za co podpisuje — inaczej zbiorcza wypłata
           jest dla niego jedną nieopisaną kwotą. -->
      <div v-if="request.items && request.items.length" class="pad-items">
        <div v-for="(it, i) in request.items" :key="i" class="pad-item">
          <span>okres od {{ formatDate(it.date) }}</span>
          <span>{{ money(it.amount) }}</span>
        </div>
      </div>

      <VueSignaturePad
        ref="pad"
        class="pad-canvas"
        :options="{ penColor: '#111', minWidth: 1.4, maxWidth: 3.2 }"
      />

      <div class="pad-actions">
        <v-btn large text @click="clearPad">Wyczyść</v-btn>
        <v-spacer />
        <v-btn large color="success" :loading="busy" @click="submit">
          Podpisuję odbiór
        </v-btn>
      </div>
      <v-alert v-if="error" type="error" text dense class="mt-2">{{ error }}</v-alert>
    </div>

    <v-snackbar v-model="thanks" :timeout="2500" color="success" top>
      Podpis przekazany do obsługi
    </v-snackbar>
  </div>
</template>

<script>
import axios from 'axios';

const TOKEN_KEY = 'signatureDeviceToken';

export default {
  name: 'SignaturePadPage',
  data: () => ({
    token: null,
    deviceName: '',
    code: '',
    request: null,
    busy: false,
    error: '',
    thanks: false,
    timer: null,
    // Ekranu nie da się zapalić z poziomu strony — żadna przeglądarka na to nie
    // pozwala. Można za to nie dać mu zgasnąć, dopóki tablet leży na ladzie.
    wakeLock: null,
    wakeLockSupported: typeof navigator !== 'undefined' && 'wakeLock' in navigator,
    audioCtx: null,
    fullscreen: false,
  }),
  created() {
    try {
      this.token = localStorage.getItem(TOKEN_KEY);
    } catch (e) {
      this.token = null;
    }
    if (this.token) {
      this.startPolling();
      this.keepScreenOn();
    }
    // System zwalnia blokadę ekranu po przejściu w tło — po powrocie trzeba
    // wziąć ją z powrotem, inaczej tablet zaśnie po pierwszym schowaniu.
    document.addEventListener('visibilitychange', this.onVisible);
    document.addEventListener('fullscreenchange', this.syncFullscreen);
    // Osobny manifest sprawia, że „dodaj do ekranu głównego" tworzy skrót
    // uruchamiający SAM ekran podpisu, a nie panel administracyjny.
    try {
      const link = document.querySelector('link[rel="manifest"]');
      if (link) {
        this.previousManifest = link.getAttribute('href');
        link.setAttribute('href', '/manifest-podpis.json');
      }
    } catch (e) { /* brak manifestu nie przeszkadza w podpisywaniu */ }
    this.syncFullscreen();
  },
  beforeDestroy() {
    clearInterval(this.timer);
    document.removeEventListener('visibilitychange', this.onVisible);
    document.removeEventListener('fullscreenchange', this.syncFullscreen);
    this.releaseWakeLock();
    try {
      const link = document.querySelector('link[rel="manifest"]');
      if (link && this.previousManifest) link.setAttribute('href', this.previousManifest);
    } catch (e) { /* przywrócenie manifestu panelu */ }
  },
  methods: {
    api(path) {
      return process.env.VUE_APP_ROOT_API + path;
    },
    money(cents) {
      return ((cents || 0) / 100).toFixed(2).replace('.', ',') + ' zł';
    },
    formatDate(d) {
      return d ? new Date(d).toLocaleDateString('pl-PL') : '';
    },
    async enterFullscreen() {
      // Pasek adresu znika dopiero na żądanie użytkownika — przeglądarki nie
      // pozwalają wejść w pełny ekran bez kliknięcia.
      try {
        const el = document.documentElement;
        if (el.requestFullscreen) await el.requestFullscreen();
        else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
      } catch (e) { /* iOS Safari tego nie wspiera — zostaje ekran główny */ }
      this.syncFullscreen();
    },
    syncFullscreen() {
      this.fullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement);
    },
    onVisible() {
      if (document.visibilityState === 'visible' && this.token) this.keepScreenOn();
    },
    async keepScreenOn() {
      if (!this.wakeLockSupported || this.wakeLock) return;
      try {
        this.wakeLock = await navigator.wakeLock.request('screen');
        this.wakeLock.addEventListener('release', () => {
          this.wakeLock = null;
        });
      } catch (e) {
        this.wakeLock = null;
      }
    },
    releaseWakeLock() {
      try {
        if (this.wakeLock) this.wakeLock.release();
      } catch (e) { /* nic nie szkodzi */ }
      this.wakeLock = null;
    },
    /**
     * Krótki sygnał, gdy pojawi się coś do podpisania — tablet leży na ladzie
     * i nikt na niego nie patrzy. Dźwięk generujemy sami, bez pliku.
     */
    beep() {
      try {
        if (!this.audioCtx) {
          const Ctx = window.AudioContext || window.webkitAudioContext;
          if (!Ctx) return;
          this.audioCtx = new Ctx();
        }
        if (this.audioCtx.state === 'suspended') this.audioCtx.resume();
        const osc = this.audioCtx.createOscillator();
        const gain = this.audioCtx.createGain();
        osc.type = 'sine';
        osc.frequency.value = 880;
        gain.gain.setValueAtTime(0.0001, this.audioCtx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.25, this.audioCtx.currentTime + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, this.audioCtx.currentTime + 0.45);
        osc.connect(gain).connect(this.audioCtx.destination);
        osc.start();
        osc.stop(this.audioCtx.currentTime + 0.5);
      } catch (e) { /* brak dźwięku nie może psuć podpisu */ }
      try {
        if (navigator.vibrate) navigator.vibrate([120, 60, 120]);
      } catch (e) { /* wibracja bywa zablokowana */ }
    },
    async pair() {
      this.error = '';
      this.busy = true;
      try {
        const res = await axios.post(this.api('signature/pair'), { code: this.code });
        this.token = res.data.data.token;
        this.deviceName = res.data.data.name;
        localStorage.setItem(TOKEN_KEY, this.token);
        this.code = '';
        // Parowanie to kliknięcie użytkownika — jedyny moment, w którym
        // przeglądarka pozwala odblokować dźwięk na przyszłość.
        this.beep();
        this.startPolling();
        this.keepScreenOn();
        this.enterFullscreen();
      } catch (e) {
        this.error = e.response?.data?.message || 'Nie udało się połączyć';
      } finally {
        this.busy = false;
      }
    },
    unpair() {
      clearInterval(this.timer);
      localStorage.removeItem(TOKEN_KEY);
      this.token = null;
      this.request = null;
    },
    startPolling() {
      clearInterval(this.timer);
      this.check();
      // Odpytujemy co dwie sekundy — kierowca stoi przy ladzie, więc ekran ma
      // się zmienić od razu, a nie po odświeżeniu.
      this.timer = setInterval(this.check, 2000);
    },
    async check() {
      if (!this.token) return;
      try {
        const res = await axios.get(this.api('signature/device/pending'), {
          headers: { 'x-signature-token': this.token },
        });
        const data = res.data.data;
        this.deviceName = data.deviceName;
        const nowe = data.request;
        // Podmieniamy tylko przy realnej zmianie, żeby nie kasować podpisu
        // składanego właśnie na ekranie.
        if ((nowe?.id || null) !== (this.request?.id || null)) {
          const pojawiloSie = !!nowe && !this.request;
          this.request = nowe;
          this.error = '';
          this.$nextTick(this.clearPad);
          if (pojawiloSie) this.beep();
        }
      } catch (e) {
        if (e.response?.status === 403) this.unpair();
      }
    },
    clearPad() {
      try {
        if (this.$refs.pad) this.$refs.pad.clearSignature();
      } catch (e) {
        /* pad jeszcze nie gotowy */
      }
    },
    async submit() {
      this.error = '';
      let signature = null;
      try {
        const res = this.$refs.pad ? this.$refs.pad.saveSignature() : null;
        if (res && !res.isEmpty) signature = res.data;
      } catch (e) {
        signature = null;
      }
      if (!signature) {
        this.error = 'Podpis jest pusty — proszę podpisać się na ekranie';
        return;
      }
      this.busy = true;
      try {
        await axios.post(
          this.api('signature/device/sign'),
          { requestId: this.request.id, signature },
          { headers: { 'x-signature-token': this.token } },
        );
        this.request = null;
        this.thanks = true;
      } catch (e) {
        this.error = e.response?.data?.message || 'Nie udało się przekazać podpisu';
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>

<style scoped>
/* Ekran obsługiwany palcem, w rękach kierowcy — duże elementy, zero nawigacji
   i niczego, co prowadziłoby dalej w głąb panelu. */
.pad-page {
  position: fixed;
  inset: 0;
  background: #12151f;
  color: #ECEFF1;
  display: flex;
  flex-direction: column;
  padding: 24px;
  overflow: hidden;
}
.pad-center { flex: 1; display: grid; place-items: center; }
.pad-card { width: min(520px, 100%); text-align: center; }
.pad-card h1 { font-size: 26px; margin: 12px 0 6px; }
.pad-lead { opacity: .75; line-height: 1.5; }
.pad-card--idle { opacity: .9; }
.pad-device { margin-top: 18px; font-size: 13px; opacity: .6; }
.pad-hint { margin-top: 14px; font-size: 13px; line-height: 1.5; color: #FFB74D; }
.pad-hint--calm { color: #90A4AE; }
.pad-idle-actions { display: flex; flex-direction: column; align-items: center; gap: 10px; margin-top: 26px; }
.pad-code >>> input {
  text-align: center;
  font-size: 40px;
  letter-spacing: 14px;
  padding: 12px 0;
}
.pad-sign { flex: 1; display: flex; flex-direction: column; }
.pad-sign-head { display: flex; align-items: flex-start; gap: 16px; }
.pad-driver { font-size: 24px; font-weight: 600; }
.pad-sub { opacity: .65; font-size: 14px; }
.pad-total { margin-left: auto; font-size: 34px; font-weight: 700; color: #69F0AE; }
.pad-items {
  margin: 14px 0 10px;
  padding: 10px 14px;
  border-radius: 10px;
  background: rgba(255,255,255,.05);
  font-size: 14px;
}
.pad-item { display: flex; justify-content: space-between; padding: 3px 0; opacity: .85; }
.pad-canvas {
  flex: 1;
  min-height: 220px;
  border-radius: 12px;
  background: #fff;
  border: 2px dashed rgba(0,0,0,.15);
}
.pad-actions { display: flex; align-items: center; gap: 12px; margin-top: 14px; }
</style>
